/**
 * Full-screen overlay (plain `View`, not `Modal`): backdrop + panel on the UI thread (`progress` + one `withTiming`).
 * Renders above the navigator from `App`’s `navShell` + `AuthSheetHost` absolute layer — avoids `Modal` mount / native host cost.
 *
 * **Model:** `progress` 0 = closed, 1 = open. Open: 0 → 1 with `enterPreset`. Close: 1 → 0 with `exitPreset` (`exit_curve` flags which curve).
 * Preset math: `overlayPanelTransform`.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { BackHandler, View, StyleSheet, useWindowDimensions, Keyboard, Pressable, Platform, type ViewStyle } from 'react-native';
import { resolveWebLayoutWidth } from '../../shared/platform/webLayout';
import Animated, { cancelAnimation, Easing, Extrapolation, interpolate, runOnJS, useAnimatedStyle, useReducedMotion, useSharedValue, withTiming } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { stripAndroidMainScreenOverlayPanelFill } from '../../shared/auth/mainScreenOverlayPanel';
import { appTheme, useThemeColors } from '../../shared/theme';
import { Clickable } from './Clickable';

export type ConfigurableOverlayPlacement = 'bottom' | 'center';
export type ConfigurableOverlayEnterPreset = 'slideUp' | 'fade' | 'scale' | 'scaleOut';
export type ConfigurableOverlayPreset = ConfigurableOverlayEnterPreset;

export interface ConfigurableOverlayProps {
  visible: boolean;
  onRequestClose: () => void;
  children: React.ReactNode;
  placement?: ConfigurableOverlayPlacement;
  maxHeightFraction?: number;
  widthFraction?: number;
  enterPreset?: ConfigurableOverlayPreset;
  exitPreset?: ConfigurableOverlayPreset;
  openDurationMs?: number;
  closeDurationMs?: number;
  hiddenSlideOffset?: number;
  backdropAccessibilityLabel: string;
  panelStyle?: ViewStyle;
  rootStyle?: ViewStyle;
  padBottomSafeArea?: boolean;
  onOpenAnimationComplete?: () => void;
  onCloseAnimationComplete?: () => void;
  panelMaxWidth?: number;
  backdropOpacityMax?: number;
  /**
   * Bottom placement: by default the panel uses `flex: 1` and grows to `maxHeight`.
   * When `true`, the panel height follows children (intrinsic) up to `maxHeight` — for compact sheets.
   */
  shrinkWrapPanel?: boolean;
  /** When `false`, hardware back and backdrop tap do not call `onRequestClose` (blocking OTP). */
  allowDismiss?: boolean;
  /** Main-screen auth on Android: keep motion, drop translucent panel fill (solid form shell instead). */
  stripAndroidPanelFill?: boolean;
  /**
   * Extra upward panel translateY (px) on top of the enter/exit transform — overlap-only keyboard
   * avoidance (never full keyboard height). Host derives this from `useFormFieldFocusScroll` /
   * `useAuthInputKeyboardAvoidance` panel-offset callbacks.
   */
  panelLiftPx?: number;
  /** Override `CENTER_PLACEMENT_BOTTOM_INSET_FRACTION` for `placement="center"`. */
  centerBottomInsetFraction?: number;
}

const CENTER_PLACEMENT_BOTTOM_INSET_FRACTION = 0.16;

const DEFAULT_SLIDE_OFF = (wh: number) => Math.min(Math.round(wh * 0.55), 520);

/** Plain color strings for worklets — never read `appTheme.colors` (Zustand getter) on the UI thread. */
export type OverlayPanelThemeColors = {
  surface: string;
  primaryUltra: string;
  surfaceFade: string;
};

const DEFAULT_OVERLAY_PANEL_COLORS: OverlayPanelThemeColors = {
  surface: '#FFFFFF',
  primaryUltra: '#E8F5E9',
  surfaceFade: 'rgba(255,255,255,0.9)',
};

/**
 * `p`: 0 = closed, 1 = open. `use_exit` 0 = `enter` preset, 1 = `exit` preset.
 */
export function overlayPanelTransform(
  p: number,
  slideOff: number,
  use_exit: number,
  enter: ConfigurableOverlayEnterPreset,
  exit: ConfigurableOverlayEnterPreset,
  vh?: number,
  theme?: OverlayPanelThemeColors,
) {
  'worklet';
  const palette = theme ?? DEFAULT_OVERLAY_PANEL_COLORS;
  const preset: ConfigurableOverlayEnterPreset = use_exit > 0.5 ? exit : enter;
  let opacity = p;
  let translateY = 0;
  let scaleY = 1;
  let bgColor = palette.surface;
  let vh_value = vh ? vh : 700;

  if (preset === 'slideUp') {
    opacity = 1;
    translateY = interpolate(p, [0, 1], [slideOff, 0], Extrapolation.CLAMP);
  } else if (preset === 'fade') {
    opacity = p;
  } else if (preset === 'scale') {
    opacity = interpolate(p, [0, 0.5], [0, 1], Extrapolation.CLAMP);
    translateY = interpolate(p, [0, 0.5, 1], [vh_value, vh_value/4, 0], Extrapolation.CLAMP);
    scaleY = interpolate(p, [0, 0, 1], [0, 1, 1], Extrapolation.CLAMP);
    // Solid fill only — never surfaceFade / translucent mid-stops (main-screen chrome bleed).
    bgColor = palette.surface;
  } else {
    // scaleOut (exit companion to scale; same p range, different curve)
    opacity = interpolate(p, [0, 0.5], [0, 1], Extrapolation.CLAMP);
    translateY = interpolate(p, [0, 0.5, 1], [vh_value, vh_value/4, 0], Extrapolation.CLAMP);
    scaleY = interpolate(p, [0, 0.5, 1], [0, 1, 1], Extrapolation.CLAMP);
    bgColor = palette.surface;
  }

  return { opacity, transform: [{ translateY }, { scaleX: 1 }, { scaleY: scaleY ?? 1 }] as const, backgroundColor: bgColor };
}

export function ConfigurableOverlay({
  visible,
  onRequestClose,
  children,
  placement = 'bottom',
  maxHeightFraction = 0.92,
  widthFraction = 0.92,
  enterPreset = 'slideUp',
  exitPreset: exit_preset_prop,
  openDurationMs = 280,
  closeDurationMs = 240,
  hiddenSlideOffset,
  backdropAccessibilityLabel,
  panelStyle,
  rootStyle,
  padBottomSafeArea = true,
  onOpenAnimationComplete,
  onCloseAnimationComplete,
  panelMaxWidth,
  backdropOpacityMax = 1,
  shrinkWrapPanel = false,
  allowDismiss = true,
  stripAndroidPanelFill = false,
  panelLiftPx = 0,
  centerBottomInsetFraction,
}: ConfigurableOverlayProps) {
  const exit_preset = exit_preset_prop ?? enterPreset;
  const colors = useThemeColors() ?? appTheme.colors;
  const insets = useSafeAreaInsets();
  const { height: wh, width: ww } = useWindowDimensions();
  /** Web: Dimensions = browser width; panels must size to the phone column shell. */
  const layout_w = resolveWebLayoutWidth(ww);
  const reduce_motion = useReducedMotion();

  const [modal_mounted, setModalMounted] = useState(visible);
  /** Start open when mounting already-visible to avoid a blank first frame (web reduce-motion / scaleY:0). */
  const progress = useSharedValue(visible ? 1 : 0);
  /** 0 = interpolate with enterPreset, 1 = with exitPreset (set synchronously when starting the matching timing). */
  const exit_curve = useSharedValue(visible ? 0 : 1);
  /**
   * Keyboard lift on the **inner** shell (not combined with enter/exit transform).
   * Rising: snap so measureInWindow settles immediately. Falling: ease to rest.
   */
  const lift_target_px = Math.max(0, panelLiftPx);
  const lift_sv = useSharedValue(lift_target_px);
  const prev_lift_target_ref = useRef(lift_target_px);

  const slide_off = useMemo(() => (hiddenSlideOffset != null ? hiddenSlideOffset : DEFAULT_SLIDE_OFF(wh)), [hiddenSlideOffset, wh]);
  const panel_width = useMemo(
    () => Math.min(layout_w * widthFraction, panelMaxWidth ?? Infinity),
    [layout_w, widthFraction, panelMaxWidth],
  );
  const bottom_pad = padBottomSafeArea ? Math.max(insets.bottom, appTheme.spacing.md) : 0;

  const on_open_ref = useRef(onOpenAnimationComplete);
  const on_close_ref = useRef(onCloseAnimationComplete);

  useEffect(() => {
    on_open_ref.current = onOpenAnimationComplete;
  }, [onOpenAnimationComplete]);
  useEffect(() => {
    on_close_ref.current = onCloseAnimationComplete;
  }, [onCloseAnimationComplete]);

  useEffect(() => {
    if (!visible) {
      cancelAnimation(lift_sv);
      lift_sv.value = 0;
      prev_lift_target_ref.current = 0;
      return;
    }
    const rising = lift_target_px >= prev_lift_target_ref.current;
    prev_lift_target_ref.current = lift_target_px;
    // Rising: snap so field measure settles; falling: ease to rest.
    if (reduce_motion || rising) {
      cancelAnimation(lift_sv);
      lift_sv.value = lift_target_px;
      return;
    }
    lift_sv.value = withTiming(lift_target_px, {
      duration: 180,
      easing: Easing.out(Easing.cubic),
    });
  }, [lift_sv, lift_target_px, reduce_motion, visible]);

  /** Stable JS callbacks — never read `.current` inside a worklet (Reanimated serializes the ref). */
  const fire_open_complete = useCallback(() => {
    on_open_ref.current?.();
  }, []);
  const fire_close_complete = useCallback(() => {
    setModalMounted(false);
    on_close_ref.current?.();
  }, []);

  /** Backdrop tap: dismiss keyboard first so the overlay stays open until the next dismiss. */
  const backdrop_dismiss_or_keyboard_first = useCallback(() => {
    const metrics = Keyboard.metrics();
    const keyboard_open =
      (typeof Keyboard.isVisible === 'function' && Keyboard.isVisible()) ||
      (metrics != null && typeof metrics.height === 'number' && metrics.height > 0);
    if (keyboard_open) {
      Keyboard.dismiss();
      return;
    }
    if (!allowDismiss) {
      return;
    }
    onRequestClose();
  }, [onRequestClose, allowDismiss]);

  /** Replaces `Modal`’s `onRequestClose` on Android (iOS: no hardware back). */
  useEffect(() => {
    if (!visible && !modal_mounted) {
      return;
    }
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      const metrics = Keyboard.metrics();
      const keyboard_open =
        (typeof Keyboard.isVisible === 'function' && Keyboard.isVisible()) ||
        (metrics != null && typeof metrics.height === 'number' && metrics.height > 0);
      if (keyboard_open) {
        Keyboard.dismiss();
        return true;
      }
      if (!allowDismiss) {
        return true;
      }
      onRequestClose();
      return true;
    });
    return () => sub.remove();
  }, [visible, modal_mounted, onRequestClose, allowDismiss]);

  useLayoutEffect(() => {
    if (visible) {
      setModalMounted(true);
    }

    cancelAnimation(progress);
    exit_curve.value = visible ? 0 : 1;

    const duration = reduce_motion ? 1 : visible ? openDurationMs : closeDurationMs;
    const easing = visible ? Easing.out(Easing.cubic) : Easing.in(Easing.cubic);
    const target = visible ? 1 : 0;
    const opening = visible;

    progress.value = withTiming(target, { duration, easing }, finished => {
      'worklet';
      if (!finished) {
        return;
      }
      if (opening) {
        runOnJS(fire_open_complete)();
      } else {
        runOnJS(fire_close_complete)();
      }
    });

    return () => {
      cancelAnimation(progress);
    };
  }, [visible, openDurationMs, closeDurationMs, reduce_motion, enterPreset, exit_preset, fire_open_complete, fire_close_complete]);

  const backdrop_anim = useAnimatedStyle(
    () => ({
      opacity: progress.value * backdropOpacityMax,
    }),
    [backdropOpacityMax],
  );

  const panel_theme = useMemo(
    (): OverlayPanelThemeColors => ({
      surface: colors.surface,
      primaryUltra: colors.primaryUltra,
      surfaceFade: colors.surfaceFade,
    }),
    [colors.primaryUltra, colors.surface, colors.surfaceFade],
  );

  const panel_anim = useAnimatedStyle(() => {
    // Web Reanimated shim: scale/translateY overlays leave a full-screen dismiss control
    // stacking above inputs (classic set1 login). Keep opacity-only motion on web.
    if (Platform.OS === 'web') {
      return {
        opacity: progress.value,
        backgroundColor: panel_theme.surface,
      };
    }
    const panel = overlayPanelTransform(progress.value, slide_off, exit_curve.value, enterPreset, exit_preset, undefined, panel_theme);
    if (stripAndroidPanelFill) {
      return stripAndroidMainScreenOverlayPanelFill(panel);
    }
    return panel;
  }, [enterPreset, exit_preset, slide_off, panel_theme, stripAndroidPanelFill]);

  const panel_lift_anim = useAnimatedStyle(() => ({
    transform: [{ translateY: -lift_sv.value }],
  }));

  const root_placement_style = useMemo((): ViewStyle => {
    if (placement === 'center') {
      // RN-web: paddingBottom + absoluteFill + flex-end parks the sheet in the
      // padding band (mostly off-screen). Margin on the panel keeps the card visible.
      if (Platform.OS === 'web') {
        return {
          justifyContent: 'flex-end',
          alignItems: 'center',
        };
      }
      return {
        justifyContent: 'flex-end',
        paddingBottom: wh * (centerBottomInsetFraction ?? CENTER_PLACEMENT_BOTTOM_INSET_FRACTION),
      };
    }
    return styles.rootBottom;
  }, [placement, wh, centerBottomInsetFraction]);

  const web_center_panel_margin_bottom =
    Platform.OS === 'web' && placement === 'center'
      ? Math.round(wh * (centerBottomInsetFraction ?? CENTER_PLACEMENT_BOTTOM_INSET_FRACTION))
      : 0;

  const combinedRootStyle = useMemo((): ViewStyle[] => {
    const horizontal_inset = (layout_w * (1 - widthFraction)) / 2;
    return [styles.rootBase, root_placement_style, { paddingHorizontal: horizontal_inset }, rootStyle as ViewStyle];
  }, [layout_w, widthFraction, root_placement_style, rootStyle]);

  const panel_max_height_px = useMemo(
    () => Math.round(wh * maxHeightFraction),
    [wh, maxHeightFraction],
  );

  if (!visible && !modal_mounted) {
    return null;
  }

  const panel_node = (
    <Animated.View
      // Important on web: keep the sheet above the dismiss control and explicitly tappable.
      pointerEvents="auto"
      style={[
        styles.panelBase,
        Platform.OS === 'web' ? styles.panelBaseWeb : null,
        {
          width: panel_width,
          maxHeight: panel_max_height_px,
          // Center fill needs a real px height so nested scroll has a viewport.
          height: placement === 'center' && !shrinkWrapPanel ? panel_max_height_px : undefined,
          paddingBottom: placement === 'bottom' ? bottom_pad : 0,
          marginBottom: undefined,
          flex: placement === 'bottom' && !shrinkWrapPanel ? 1 : 0,
          alignSelf: Platform.OS === 'web' ? ('center' as const) : ('stretch' as const),
          // Classic center auth cards: solid underlay (no main-screen chrome bleed).
          ...(placement === 'center' && !stripAndroidPanelFill ? { backgroundColor: colors.surface } : null),
        },
        panelStyle,
        panel_anim,
      ]}>
      <Animated.View
        style={[
          shrinkWrapPanel ? styles.panelInnerShrink : styles.panelInnerFill,
          placement === 'center' && !stripAndroidPanelFill ? { backgroundColor: colors.surface } : null,
          panel_lift_anim,
        ]}
        collapsable={false}>
        {children}
      </Animated.View>
    </Animated.View>
  );

  /**
   * Web: nest the panel inside a top-layer box-none shell so empty space dismisses,
   * while the sheet itself (not the absoluteFill Pressable) owns pointer events.
   * Native keeps the prior sibling stacking (Pressable then panel).
   */
  if (Platform.OS === 'web') {
    return (
      <View style={styles.host} pointerEvents="box-none" collapsable={false}>
        <Animated.View
          pointerEvents="none"
          style={[StyleSheet.absoluteFill, styles.backdropLayer, { backgroundColor: colors.backdrop }, backdrop_anim]}
        />
        <Pressable
          style={[StyleSheet.absoluteFill, styles.backdropPressable]}
          onPress={backdrop_dismiss_or_keyboard_first}
          accessibilityRole="button"
          accessibilityLabel={backdropAccessibilityLabel}
        />
        {/*
          Do NOT use a full-screen box-none wrapper on web — RN-web maps `box-none` to an
          invalid CSS value, so the dismiss Pressable stays the hit target over inputs.
          Anchor only the sheet rect above the dismiss control.
        */}
        <View
          pointerEvents="box-none"
          style={[
            styles.webPanelAnchor,
            {
              paddingHorizontal: (layout_w * (1 - widthFraction)) / 2,
              bottom: placement === 'center' ? web_center_panel_margin_bottom : 0,
            },
            rootStyle as ViewStyle,
          ]}
          collapsable={false}>
          {panel_node}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.host} pointerEvents="box-none" collapsable={false}>
      <Animated.View
        pointerEvents="none"
        style={[StyleSheet.absoluteFill, styles.backdropLayer, { backgroundColor: colors.backdrop }, backdrop_anim]}
      />
      <Pressable
        style={[StyleSheet.absoluteFill, styles.backdropPressable]}
        onPress={backdrop_dismiss_or_keyboard_first}
        accessibilityRole="button"
        accessibilityLabel={backdropAccessibilityLabel}
      />
      <View
        pointerEvents="box-none"
        style={[StyleSheet.absoluteFill, styles.panelLayer, ...combinedRootStyle]}
        collapsable={false}>
        {panel_node}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  host: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1,
  },
  rootBase: {
    flex: 1,
    alignItems: 'center',
  },
  rootBottom: {
    justifyContent: 'flex-end',
  },
  backdropLayer: {
    zIndex: 0,
  },
  backdropPressable: {
    zIndex: 1,
  },
  panelLayer: {
    zIndex: 10,
  },
  /** Web-only: sheet anchor above dismiss — height follows content, not the full window. */
  webPanelAnchor: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 20,
    alignItems: 'center',
    elevation: 20,
  },
  panelBase: {
    minHeight: 0,
    backgroundColor: 'transparent',
    overflow: 'hidden',
  },
  panelBaseWeb: {
    zIndex: 21,
    position: 'relative',
    elevation: 21,
  },
  /** Intrinsic children — do not flex-fill (avoids 0-height collapse in shrink-wrap). */
  panelInnerShrink: {
    width: '100%',
  },
  panelInnerFill: {
    width: '100%',
    flex: 1,
    minHeight: 0,
  },
});
